import type { AxiosInstance } from 'axios';

/**
 * Read-only Senvol material catalog.
 *
 * Backs the "choose a specific material" picker: a printability suggestion names
 * a material FAMILY (`materials.general_similar_material`), and this resolves that
 * family into the concrete branded materials inside it — the same rows suppliers
 * declare capabilities against, which is what makes matching line up.
 */
export interface MaterialCatalogItem {
  id: number;
  name: string;
  manufacturer: string | null;
  /** The family this material rolls up to — the supplier-matching key. */
  family: string | null;
  amProcess: string | null;
  buildMaterialType: string | null;
  availability: string | null;
  isRetired: boolean;
  /** Solid-phase density, for estimating a printed part's mass. */
  densityGCm3: number | null;
}

export interface MaterialFamilyItem {
  family: string;
  materialCount: number;
  amProcesses: string[];
  /**
   * Median density across the family's members, or null when none record one.
   * Members genuinely differ, so this is only ever a starting estimate.
   */
  medianDensityGCm3: number | null;
}

/** Mirrors `ProcessCategory` in the API's shared messaging contract. */
export type ProcessCategoryParam =
  | 'material_extrusion'
  | 'vat_photopolymerization'
  | 'powder_bed_fusion_polymer'
  | 'powder_bed_fusion_metal'
  | 'material_jetting'
  | 'binder_jetting';

export interface MaterialListParams {
  family?: string;
  processCategory?: ProcessCategoryParam;
  search?: string;
  page?: number;
  limit?: number;
  type?: 'additive' | 'subtractive';
}

export interface MaterialListResult {
  data: MaterialCatalogItem[];
  total: number;
  hasMore: boolean;
}

export function createMaterialsService(client: AxiosInstance) {
  return {
    async listFamilies(params: {
      processCategory?: ProcessCategoryParam;
      search?: string;
      type?: 'additive' | 'subtractive';
    }): Promise<MaterialFamilyItem[]> {
      const { data } = await client.get<{ data: MaterialFamilyItem[] }>('/materials/families', {
        params,
      });
      return data.data;
    },

    async list(params: MaterialListParams): Promise<MaterialListResult> {
      const { data } = await client.get<{
        data: MaterialCatalogItem[];
        meta: { total?: number; hasMore?: boolean };
      }>('/materials', { params });
      return {
        data: data.data,
        total: data.meta?.total ?? data.data.length,
        hasMore: data.meta?.hasMore ?? false,
      };
    },
  };
}

export type MaterialsService = ReturnType<typeof createMaterialsService>;
